import { SoccerStages } from '@adflow/types';
import { resolveMatchStatusDisplay } from '@adflow/utils';

import { MockPreviewValues } from '../components/previewConstants';
import type { SoccerFixture, SoccerFullTimeResultOdds } from './types';

const getPreviewLocale = (): string =>
  (typeof navigator !== 'undefined' && navigator.language) || 'en-GB';

export type SoccerPreviewValue = string | Date | readonly [number, number];
export type SoccerPreviewData = Partial<Record<string, SoccerPreviewValue>>;

export const FULL_TIME_RESULT_PREVIEW_DATA_POINT_NAMES = [
  'MARKET_NAME',
  'HOME_ODDS',
  'HOME_PERCENT_BETS',
  'HOME_PERCENT_MONEY',
  'AWAY_ODDS',
  'AWAY_PERCENT_BETS',
  'AWAY_PERCENT_MONEY',
  'DRAW_ODDS',
  'DRAW_PERCENT_BETS',
  'DRAW_PERCENT_MONEY',
  'COMPETITOR_ODDS',
  'OPPONENT_ODDS',
  'INSIGHT_TEXT',
] as const;

type FullTimeResultPreviewName = (typeof FULL_TIME_RESULT_PREVIEW_DATA_POINT_NAMES)[number];

const getFixtureOwnedFullTimeResultOdds = (
  key: FullTimeResultPreviewName,
  fullTimeResultOdds?: SoccerFullTimeResultOdds,
): SoccerPreviewValue | undefined => {
  if (!fullTimeResultOdds) {
    return undefined;
  }

  switch (key) {
    case 'HOME_ODDS':
    case 'AWAY_ODDS':
    case 'DRAW_ODDS':
    case 'COMPETITOR_ODDS':
    case 'OPPONENT_ODDS':
      return fullTimeResultOdds[key];
    default:
      return undefined;
  }
};

const buildFullTimeResultPreviewData = (
  isSupported: boolean,
  fullTimeResultOdds?: SoccerFullTimeResultOdds,
): SoccerPreviewData =>
  FULL_TIME_RESULT_PREVIEW_DATA_POINT_NAMES.reduce<SoccerPreviewData>((data, key) => {
    const fixtureOwnedOdds = isSupported
      ? getFixtureOwnedFullTimeResultOdds(key, fullTimeResultOdds)
      : undefined;

    if (fixtureOwnedOdds !== undefined) {
      data[key] = fixtureOwnedOdds;
      return data;
    }

    data[key] = isSupported
      ? (MockPreviewValues[key as FullTimeResultPreviewName] as SoccerPreviewValue)
      : '';
    return data;
  }, {});

const buildFullScore = (homeScore: string, awayScore: string) => `${homeScore} - ${awayScore}`;

type PenaltyDataPointPrefix = 'LIVE_TEAM' | 'POST_TEAM';

const buildPenaltyPreviewData = (
  prefix: PenaltyDataPointPrefix,
  homePenaltyScore: string | undefined,
  awayPenaltyScore: string | undefined,
): SoccerPreviewData => ({
  ...(homePenaltyScore !== undefined ? { [`${prefix}_HOME_PENALTY_SCORE`]: homePenaltyScore } : {}),
  ...(awayPenaltyScore !== undefined ? { [`${prefix}_AWAY_PENALTY_SCORE`]: awayPenaltyScore } : {}),
});

export const resolvePreviewData = (fixture: SoccerFixture): SoccerPreviewData => {
  const commonData: SoccerPreviewData = {
    TEAM_HOME_NAME: fixture.homeTeamName,
    TEAM_AWAY_NAME: fixture.awayTeamName,
    TEAM_HOME_ABBREVIATION: fixture.homeTeamAbbreviation,
    TEAM_AWAY_ABBREVIATION: fixture.awayTeamAbbreviation,
    TEAM_HOME_COMPETITOR_ID: fixture.homeCompetitorId,
    TEAM_AWAY_COMPETITOR_ID: fixture.awayCompetitorId,
    EVENT_ROUND_NAME: '',
  };

  const locale = getPreviewLocale();

  switch (fixture.stage) {
    case SoccerStages.PRE_EVENT:
      return {
        ...commonData,
        MATCH_STATUS: resolveMatchStatusDisplay(
          fixture.matchStatus,
          undefined,
          locale,
          { separator: ':' },
          undefined,
          undefined,
        ),
        DAY: fixture.kickoffAt,
        DATE: fixture.kickoffAt,
        MONTH: fixture.kickoffAt,
        TIME: fixture.kickoffAt,
        COUNTDOWN: fixture.kickoffAt,
        ...buildFullTimeResultPreviewData(true, fixture.fullTimeResultOdds),
      };
    case SoccerStages.LIVE:
      return {
        ...commonData,
        MATCH_STATUS: resolveMatchStatusDisplay(
          fixture.matchStatus,
          fixture.clock,
          locale,
          { separator: ':' },
          fixture.homePenaltyScore,
          fixture.awayPenaltyScore,
        ),
        LIVE_TEAM_HOME_SCORE: fixture.homeScore,
        LIVE_TEAM_AWAY_SCORE: fixture.awayScore,
        LIVE_FULL_SCORE: buildFullScore(fixture.homeScore, fixture.awayScore),
        ...buildPenaltyPreviewData('LIVE_TEAM', fixture.homePenaltyScore, fixture.awayPenaltyScore),
        ...buildFullTimeResultPreviewData(true, fixture.fullTimeResultOdds),
      };
    case SoccerStages.LIVE_GOAL:
      return {
        ...commonData,
        MATCH_STATUS: resolveMatchStatusDisplay(
          fixture.matchStatus,
          fixture.clock,
          locale,
          { separator: ':' },
          undefined,
          undefined,
        ),
        LIVE_TEAM_HOME_SCORE: fixture.homeScore,
        LIVE_TEAM_AWAY_SCORE: fixture.awayScore,
        LIVE_FULL_SCORE: buildFullScore(fixture.homeScore, fixture.awayScore),
        MOMENT_GOAL_SCORER_NAME: fixture.goalScorerName,
        MOMENT_GOAL_MATCH_TIME: fixture.goalMatchTime,
        MOMENT_GOAL_TEAM_COMPETITOR_ID: fixture.goalTeamCompetitorId,
        ...buildFullTimeResultPreviewData(false),
      };
    case SoccerStages.POST_EVENT:
      return {
        ...commonData,
        MATCH_STATUS: resolveMatchStatusDisplay(
          fixture.matchStatus,
          undefined,
          locale,
          { separator: ':' },
          fixture.homePenaltyScore,
          fixture.awayPenaltyScore,
        ),
        POST_TEAM_HOME_SCORE: fixture.homeScore,
        POST_TEAM_AWAY_SCORE: fixture.awayScore,
        POST_FULL_SCORE: buildFullScore(fixture.homeScore, fixture.awayScore),
        POST_EVENT_OUTCOME: fixture.postEventOutcome,
        ...buildPenaltyPreviewData('POST_TEAM', fixture.homePenaltyScore, fixture.awayPenaltyScore),
        ...buildFullTimeResultPreviewData(false),
      };
    case SoccerStages.POST_WINNER:
      return {
        ...commonData,
        MATCH_STATUS: resolveMatchStatusDisplay(
          fixture.matchStatus,
          undefined,
          locale,
          { separator: ':' },
          fixture.homePenaltyScore,
          fixture.awayPenaltyScore,
        ),
        POST_TEAM_HOME_SCORE: fixture.homeScore,
        POST_TEAM_AWAY_SCORE: fixture.awayScore,
        POST_FULL_SCORE: buildFullScore(fixture.homeScore, fixture.awayScore),
        POST_EVENT_OUTCOME: fixture.postEventOutcome,
        POST_WINNER_TEAM_NAME: fixture.winnerTeamName,
        POST_WINNER_TEAM_COMPETITOR_ID: fixture.winnerTeamCompetitorId,
        ...buildPenaltyPreviewData('POST_TEAM', fixture.homePenaltyScore, fixture.awayPenaltyScore),
        ...buildFullTimeResultPreviewData(false),
      };
  }
};
